import { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeBusiness, setActiveBusinessState] = useState(null);
  const [businesses, setBusinesses] = useState([]);
  const [upgradeModal, setUpgradeModal] = useState({ open: false, errorDetail: null });

  const activeBusinessRef = useRef(activeBusiness);
  activeBusinessRef.current = activeBusiness;

  const api = useMemo(() => {
    const instance = axios.create({ baseURL: API, withCredentials: true });

    // Inject X-Business-Id header on every request
    instance.interceptors.request.use((config) => {
      const bizId = activeBusinessRef.current?.business_id;
      if (bizId) {
        config.headers["X-Business-Id"] = bizId;
      }
      return config;
    });

    // Global response interceptor: auto-trigger upgrade modal on 429/403 with upgrade_required
    instance.interceptors.response.use(
      (response) => response,
      (error) => {
        const detail = error.response?.data?.detail;
        if (detail && typeof detail === "object" && detail.upgrade_required) {
          setUpgradeModal({ open: true, errorDetail: detail });
        }
        return Promise.reject(error);
      }
    );

    return instance;
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setUpgradeModal({ open: false, errorDetail: null });
  }, []);

  const openUpgradeModal = useCallback((errorDetail = null) => {
    setUpgradeModal({ open: true, errorDetail });
  }, []);

  const loadBusinesses = useCallback(async () => {
    try {
      const resp = await api.get("/businesses");
      setBusinesses(resp.data.businesses || []);
      return resp.data;
    } catch { return null; }
  }, [api]);

  const checkAuth = useCallback(async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);

      // Load businesses and set active
      const bizData = await loadBusinesses();
      if (bizData && response.data.active_business_id) {
        const active = (bizData.businesses || []).find(
          b => b.business_id === response.data.active_business_id
        );
        if (active) setActiveBusinessState(active);
      }
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, [api, loadBusinesses]);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const applyAuthResponse = async (data) => {
    setUser(data);
    const bizData = await loadBusinesses();
    if (bizData && data.active_business_id) {
      const active = (bizData.businesses || []).find(
        b => b.business_id === data.active_business_id
      );
      if (active) setActiveBusinessState(active);
    }
    return data;
  };

  const login = async (email, password) => {
    const response = await api.post("/auth/login", { email, password });
    return applyAuthResponse(response.data);
  };

  const register = async (name, email, password) => {
    const response = await api.post("/auth/register", { name, email, password });
    return applyAuthResponse(response.data);
  };

  const switchBusiness = async (businessId) => {
    try {
      const resp = await api.post("/businesses/switch", { business_id: businessId });
      const biz = businesses.find(b => b.business_id === businessId);
      if (biz) setActiveBusinessState(biz);

      // Refresh user to get updated active_business_id
      const meResp = await api.get("/auth/me");
      setUser(meResp.data);

      return resp.data;
    } catch (e) {
      throw e;
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(null);
    setActiveBusinessState(null);
    setBusinesses([]);
  };

  const refreshUser = async () => {
    try {
      const response = await api.get("/auth/me");
      setUser(response.data);
    } catch { /* ignore */ }
  };

  return (
    <AuthContext.Provider value={{
      user, loading, login, register, logout, refreshUser, api,
      activeBusiness, businesses, switchBusiness, loadBusinesses,
      upgradeModal, closeUpgradeModal, openUpgradeModal,
    }}>
      {children}
    </AuthContext.Provider>
  );
};
